import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  FacebookAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithPopup,
} from 'firebase/auth';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { environment } from 'src/environments/environment';

/**
 * Page "passerelle" (bridge) pour le SSO desktop (app Tauri).
 *
 * Contexte : la webview Tauri bloque les popups Firebase (`auth/popup-blocked`).
 * On ouvre donc cette page (hébergée sur un vrai domaine HTTPS) dans le
 * navigateur système. Ici, `signInWithPopup` fonctionne normalement ; on
 * récupère le `_tokenResponse` Firebase (idToken + email/noms) puis on renvoie
 * l'utilisateur vers l'app desktop via le schéma custom `narratheque://`.
 *
 * L'idToken est ensuite consommé tel quel par `POST /auth/register_sso` côté
 * back : aucune modification backend n'est requise pour le login.
 */
@Component({
  selector: 'app-desktop-sso',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="desktop-sso">
      <div class="card">
        @if (status === 'pending') {
          <h2>Connexion en cours…</h2>
          <p>Finalisez l'authentification dans la fenêtre Google, puis revenez
            à l'application Narrathèque.</p>
        } @else if (status === 'success') {
          <h2>✅ Connecté</h2>
          <p>Revenez à l'application Narrathèque.</p>
          <button class="return-btn" (click)="returnToApp()">
            ← Revenir à l'application
          </button>
          <p class="hint">
            La redirection est normalement automatique. Si rien ne se passe,
            cliquez sur le bouton ci-dessus.
          </p>
        } @else {
          <h2>❌ Échec de la connexion</h2>
          <p>{{ error }}</p>
          <p>Vous pouvez fermer cet onglet et réessayer depuis l'application.</p>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .desktop-sso {
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: #f6f7fb;
        font-family: system-ui, -apple-system, sans-serif;
      }
      .card {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 6px 30px rgba(0, 0, 0, 0.08);
        padding: 2.5rem 2rem;
        max-width: 440px;
        text-align: center;
      }
      .card h2 {
        margin: 0 0 0.75rem;
        font-size: 1.25rem;
      }
      .card p {
        color: #555;
        margin: 0.25rem 0;
        line-height: 1.45;
      }
      .return-btn {
        margin: 1rem 0;
        padding: 0.7rem 1.4rem;
        font-size: 1rem;
        font-weight: 600;
        color: #fff;
        background: #4f46e5;
        border: none;
        border-radius: 10px;
        cursor: pointer;
        transition: background 0.15s ease;
      }
      .return-btn:hover {
        background: #4338ca;
      }
      .hint {
        font-size: 0.85rem;
        color: #888;
        margin-top: 0.75rem;
      }
    `,
  ],
})
export class DesktopSsoComponent implements OnInit {
  status: 'pending' | 'success' | 'error' = 'pending';
  error = '';
  /** Payload en attente de renvoi vers l'app (pour le bouton de secours). */
  private pending: { state: string; payload: Record<string, any> } | null =
    null;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    const provider = (this.route.snapshot.queryParamMap.get('provider') ??
      '') as 'google' | 'facebook' | 'apple';
    const state = this.route.snapshot.queryParamMap.get('state') ?? '';

    if (!provider || !state) {
      this.fail('Paramètres manquants (provider/state).');
      return;
    }

    this.run(provider, state);
  }

  private async run(
    provider: 'google' | 'facebook' | 'apple',
    state: string,
  ): Promise<void> {
    try {
      // Garde : l'app par défaut peut ne pas encore exister (page publique,
      // injection possible avant l'initialisation d'@angular/fire).
      const auth = getAuth(
        getApps().length ? getApp() : initializeApp(environment.firebaseConfig)
      );
      let result;
      if (provider === 'google') {
        result = await signInWithPopup(auth, new GoogleAuthProvider());
      } else if (provider === 'facebook') {
        result = await signInWithPopup(auth, new FacebookAuthProvider());
      } else {
        const p = new OAuthProvider('apple.com');
        result = await signInWithPopup(auth, p);
      }

      const tokenResponse = (result as any)?._tokenResponse ?? {};
      // On ne garde que ce dont le desktop a besoin (idToken + identité).
      const payload = {
        idToken: tokenResponse.idToken,
        email: tokenResponse.email,
        firstName: tokenResponse.firstName,
        lastName: tokenResponse.lastName,
      };

      if (!payload.idToken) {
        this.fail("Aucun idToken Firebase n'a été renvoyé.");
        return;
      }

      this.status = 'success';
      this.pending = { state, payload };
      // Tentative d'auto-redirect. NB : certains navigateurs bloquent la
      // navigation vers un schéma custom hors geste utilisateur direct ; le
      // bouton "Revenir à l'application" est le chemin fiable.
      setTimeout(() => this.redirect(state, payload), 400);
    } catch (e: any) {
      const code = e?.code || '';
      const msg =
        code === 'auth/popup-closed-by-user'
          ? 'La fenêtre de connexion a été fermée.'
          : code === 'auth/cancelled-popup-request'
            ? 'Une autre demande de connexion est en cours.'
            : e?.message || 'Erreur inconnue lors de la connexion.';
      this.fail(msg, state);
    }
  }

  private redirect(state: string, payload: Record<string, any>): void {
    const encoded = this.base64UrlEncode(JSON.stringify(payload));
    window.location.href = `narratheque://sso?state=${encodeURIComponent(
      state,
    )}&payload=${encoded}`;
  }

  /**
   * Bouton de secours : re-déclenche la redirection vers l'app desktop depuis
   * un geste utilisateur explicite (fiable là où l'auto-redirect est bloqué).
   */
  returnToApp(): void {
    if (this.pending) {
      this.redirect(this.pending.state, this.pending.payload);
    }
  }

  private fail(message: string, state?: string): void {
    this.status = 'error';
    this.error = message;
    // On renvoie aussi l'erreur vers l'app desktop pour libérer son attente.
    if (state) {
      setTimeout(() => {
        window.location.href = `narratheque://sso?state=${encodeURIComponent(
          state,
        )}&error=${encodeURIComponent(message)}`;
      }, 1500);
    }
  }

  /** UTF-8 safe base64url. */
  private base64UrlEncode(str: string): string {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
